import React, { useState } from 'react';import { useNavigate, useLocation } from 'react-router-dom';import API_BASE_URL from '../config';import styles from '../css-isolated/jo_pdl_cell.module.css';import ChangePasswordModal from './change_pass';
import authFetch from '../utils/authFetch';

function PdlCellUpdate() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [cells, setCells] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [pendingTheme, setPendingTheme] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchData, setSearchData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    akaNickname: '',
    cellId: ''
  });

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    if (isNaN(birthDate.getTime())) return 'N/A';
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 0 ? age : 'N/A';
  };

  // Sorting logic
  const sortedResults = React.useMemo(() => {
    if (!sortColumn) return searchResults;

    const sorted = [...searchResults].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'lastName':
          aValue = a.last_name || '';
          bValue = b.last_name || '';
          break;
        case 'firstName':
          aValue = a.first_name || '';
          bValue = b.first_name || '';
          break;
        case 'middleName':
          aValue = a.middle_name || '';
          bValue = b.middle_name || '';
          break;
        case 'aka':
          aValue = a.alias_nickname || '';
          bValue = b.alias_nickname || '';
          break;
        case 'age':
          aValue = calculateAge(a.date_of_birth);
          bValue = calculateAge(b.date_of_birth);
          // Handle 'N/A' values - treat as very high number so they appear at the end
          aValue = aValue === 'N/A' ? 999999 : Number(aValue);
          bValue = bValue === 'N/A' ? 999999 : Number(bValue);
          // Direct numeric comparison for age
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          return 0;
      }

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [searchResults, sortColumn, sortDirection]);

  // Pagination constants
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(sortedResults.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPageData = sortedResults.slice(startIndex, endIndex);

  // Reset to page 1 when search results change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchResults]);

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle sort direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get tooltip text for column sorting
  const getSortTooltip = (column) => {
    if (sortColumn === column && sortDirection === 'asc') {
      return 'Sort Z to A';
    }
    return 'Sort A to Z';
  };

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.3, marginLeft: '4px' }}>
          <path d="M7 10L12 5L17 10M7 14L12 19L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Update time every second
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load user data from localStorage
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Fetch cells on component mount
  React.useEffect(() => {
    fetchCells();
  }, []);

  // Auto-filter unassigned PDLs when navigated from the KPI card
  React.useEffect(() => {
    if (!location.state?.autoLoad) return;
    const fetchUnassigned = async () => {
      setIsLoading(true);
      try {
        const response = await authFetch(`${API_BASE_URL}/api/hr-info/search?cellId=unassigned`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        const nonArchivedData = data.filter(record => !record.final_remarks);
        setSearchData(prev => ({ ...prev, cellId: 'unassigned' }));
        setSearchResults(nonArchivedData);
      } catch (error) {
        console.error('Error fetching unassigned PDLs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUnassigned();
  }, [location.state]);

  const fetchCells = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/cells`);
      if (!response.ok) {
        throw new Error('Failed to fetch cells');
      }
      const result = await response.json();
      // Handle both direct array and wrapped response
      const cellsData = result.data || result;
      setCells(Array.isArray(cellsData) ? cellsData : []);
    } catch (error) {
      console.error('Error fetching cells:', error);
      setCells([]);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleDarkMode = (newMode) => {
    setPendingTheme(newMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    // Close modal immediately
    setShowThemeModal(false);
    
    // Update local state and storage
    setIsDarkMode(pendingTheme);
    localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
    
    try {
      // Update database in background
      if (userData && userData.id) {
        await authFetch(`${API_BASE_URL}/api/update-theme`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userData.id,
            is_darkmode: pendingTheme ? 1 : 0
          })
        });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    // Preserve theme before clearing storage
    const currentTheme = localStorage.getItem('theme');
    
    // Determine redirect path based on user type
    const storedUser = localStorage.getItem('user');
    let redirectPath = '/login-jailofficers'; // Default to jail officer login
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        // Check if user is superuser
        if (user.user_type === 'superuser' || user.user_type === 'super_user') {
          redirectPath = '/login_superuser';
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    localStorage.clear();
    sessionStorage.clear();
    
    // Restore theme
    if (currentTheme) {
      localStorage.setItem('theme', currentTheme);
    }
    
    setShowLogoutModal(false);
    navigate(redirectPath, { replace: true });
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(`.${styles.profileSection}`)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Filter out empty values from searchData
      const filteredParams = Object.entries(searchData)
        .filter(([_, value]) => value !== '')
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
      
      const queryParams = new URLSearchParams(filteredParams).toString();
      const response = await authFetch(`${API_BASE_URL}/api/hr-info/search?${queryParams}`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      
      // Filter out archived records (those with final_remarks)
      const nonArchivedData = data.filter(record => !record.final_remarks);
      setSearchResults(nonArchivedData);
    } catch (error) {
      console.error('Error searching records:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className={`${styles.systemApp} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      {/* System Header */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.systemInfo}>
            <div className={styles.systemTime}>{currentTime.toLocaleTimeString()}</div>
            <div className={styles.systemUser}>{formatDate(currentTime)}</div>
          </div>
          <div className={styles.profileSection}>
            <div className={styles.profileIcon} onClick={toggleDropdown}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            {isDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={styles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                <div className={styles.dropdownDivider}></div>
                <button className={styles.dropdownItem} onClick={() => toggleDarkMode(!isDarkMode)}>
                  {isDarkMode ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill="currentColor"/>
                    </svg>
                  )}
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={styles.dropdownItem} onClick={() => setShowChangePasswordModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                <div className={styles.dropdownDivider}></div>
                <button className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className={styles.jbrSearchPage}>
        {/* Header with home button */}
        <div className={styles.jbrHeader}>
          <button onClick={() => navigate('/')} className={styles.jbrBackBtn}>← Back to Dashboard</button>
          <h1 className={styles.jbrTitle}>PDL Cell Assignment</h1>
        </div>

        {/* Search Form */}
        <div className={styles.searchCard}>
          <div className={styles.searchHeader}>
            <div className={styles.searchHeaderIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.searchHeaderText}>
              <h2>Search PDL for Cell Assignment</h2>
              <p>Find PDL records to update cell assignments</p>
            </div>
          </div>
          
          <form onSubmit={handleSearch} className={styles.searchForm}>
            <div className={styles.searchFormGrid}>
              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={searchData.lastName}
                    onChange={handleInputChange}
                    className={styles.searchInput}
                    placeholder="Enter last name"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={searchData.firstName}
                    onChange={handleInputChange}
                    className={styles.searchInput}
                    placeholder="Enter first name"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>Middle Name</label>
                  <input
                    type="text"
                    name="middleName"
                    value={searchData.middleName}
                    onChange={handleInputChange}
                    className={styles.searchInput}
                    placeholder="Enter middle name"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>AKA/Nickname</label>
                  <input
                    type="text"
                    name="akaNickname"
                    value={searchData.akaNickname}
                    onChange={handleInputChange}
                    className={styles.searchInput}
                    placeholder="Enter AKA/nickname"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2"/>
                    <line x1="9" y1="21" x2="9" y2="9" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>Cell</label>
                  <select
                    name="cellId"
                    value={searchData.cellId}
                    onChange={handleInputChange}
                    className={styles.searchInput}
                  >
                    <option value="">All Cells</option>
                    <option value="unassigned">Unassigned</option>
                    {Array.isArray(cells) && cells.map((cell) => (
                      <option key={cell.id} value={cell.id}>
                        {cell.cell_no} - {cell.type_of_cell}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.searchActions}>
              <button type="submit" className={styles.searchButton} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className={styles.searchSpinner}></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Search Records
                  </>
                )}
              </button>
              
              <button 
                type="button" 
                className={styles.clearButton}
                onClick={() => setSearchData({ lastName: '', firstName: '', middleName: '', akaNickname: '', cellId: '' })}
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className={styles.resultsSection}>
            <div className={styles.resultsHeader}>
              <div className={styles.resultsHeaderIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 7V17C4 17.5304 4.21071 18.0391 4.58579 18.4142C4.96086 18.7893 5.46957 19 6 19H18C18.5304 19 19.0391 18.7893 19.4142 18.4142C19.7893 18.0391 20 17.5304 20 17V7C20 6.46957 19.7893 5.96086 19.4142 5.58579C19.0391 5.21071 18.5304 5 18 5H6C5.46957 5 4.96086 5.21071 4.58579 5.58579C4.21071 5.96086 4 6.46957 4 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.resultsHeaderText}>
                <h3>Searching...</h3>
                <p>Please wait while we search for PDL records</p>
              </div>
            </div>
            <div className={styles.resultsContainer}>
              <div className={styles.loadingState}>
                <div className={styles.loadingSpinner}></div>
                <p>Searching PDL records...</p>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {!isLoading && searchResults.length > 0 && (
          <div className={styles.resultsSection}>
            <div className={styles.resultsHeader}>
              <div className={styles.resultsHeaderIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 7V17C4 17.5304 4.21071 18.0391 4.58579 18.4142C4.96086 18.7893 5.46957 19 6 19H18C18.5304 19 19.0391 18.7893 19.4142 18.4142C19.7893 18.0391 20 17.5304 20 17V7C20 6.46957 19.7893 5.96086 19.4142 5.58579C19.0391 5.21071 18.5304 5 18 5H6C5.46957 5 4.96086 5.21071 4.58579 5.58579C4.21071 5.96086 4 6.46957 4 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.resultsHeaderText}>
                <h3>Search Results</h3>
                <p>Found {searchResults.length} record(s)</p>
              </div>
            </div>
            
            <div className={styles.resultsTableContainer}>
              <table className={styles.resultsTable}>
                <thead>
                  <tr>
                    <th className={styles.tableHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Current Cell
                      </div>
                    </th>
                    <th className={styles.tableHeader} onClick={() => handleSort('lastName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('lastName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Last Name
                        {renderSortIndicator('lastName')}
                      </div>
                    </th>
                    <th className={styles.tableHeader} onClick={() => handleSort('firstName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('firstName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        First Name
                        {renderSortIndicator('firstName')}
                      </div>
                    </th>
                    <th className={styles.tableHeader} onClick={() => handleSort('middleName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('middleName')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Middle Name
                        {renderSortIndicator('middleName')}
                      </div>
                    </th>
                    <th className={styles.tableHeader} onClick={() => handleSort('aka')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('aka')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        AKA
                        {renderSortIndicator('aka')}
                      </div>
                    </th>
                    <th className={styles.tableHeader} onClick={() => handleSort('age')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('age')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Age
                        {renderSortIndicator('age')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((record, index) => (
                    <tr key={index} className={styles.tableRow}>
                      <td className={styles.tableCell}>
                        <span className={`${styles.cellBadge} ${record.cell ? styles.cellBadgeAssigned : styles.cellBadgeUnassigned}`}>
                          {record.cell?.cell_no || 'Unassigned'}
                        </span>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.cellText}>{record.last_name || 'N/A'}</span>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.cellText}>{record.first_name || 'N/A'}</span>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.cellText}>{record.middle_name || 'N/A'}</span>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.cellText}>{record.alias_nickname || 'N/A'}</span>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.cellText}>{calculateAge(record.date_of_birth)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className={styles.paginationContainer}>
                <button
                  className={styles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Previous
                </button>

                <div className={styles.paginationInfo}>
                  <span className={styles.paginationText}>
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                  <span className={styles.paginationDivider}>•</span>
                  <span className={styles.paginationText}>
                    Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(endIndex, searchResults.length)}</strong> of <strong>{searchResults.length}</strong> records
                  </span>
                </div>

                <button
                  className={styles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && searchResults.length === 0 && searchData.lastName === '' && searchData.firstName === '' && searchData.middleName === '' && searchData.akaNickname === '' && (
          <div className={styles.emptyState}>
            <svg className={styles.emptyStateIcon} width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h4>No Search Performed</h4>
            <p>Enter search criteria above to find PDL records for cell assignment</p>
          </div>
        )}

        {/* No Results */}
        {!isLoading && searchResults.length === 0 && (searchData.lastName !== '' || searchData.firstName !== '' || searchData.middleName !== '' || searchData.akaNickname !== '') && (
          <div className={styles.emptyState}>
            <svg className={styles.emptyStateIcon} width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 10L14 14M14 10L10 14M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h4>No Results Found</h4>
            <p>No PDL records match your search criteria. Try different search terms.</p>
          </div>
        )}

        {/* Change Password Modal */}
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          userData={JSON.parse(localStorage.getItem('user'))}
          onSuccess={(msg) => {
            setSuccessMessage(msg);
            setShowSuccessModal(true);
          }}
          onError={(msg) => {
            setErrorMessage(msg);
            setShowErrorModal(true);
          }}
        />

        {/* Success Modal */}
        {showSuccessModal && (
          <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
            <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.successIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Success</h3>
              <p>{successMessage}</p>
              <button className={styles.successBtn} onClick={() => setShowSuccessModal(false)}>
                OK
              </button>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
            <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.errorIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Error</h3>
              <p>{errorMessage}</p>
              <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Theme Change Confirmation Modal */}
        {showThemeModal && (
          <div className={styles.modalOverlay} onClick={cancelThemeChange}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{borderColor: '#f59e0b', background: isDarkMode ? '#1e293b' : 'white'}}>
              <div className={styles.themeIcon} style={{background: 'rgba(245, 158, 11, 0.1)'}}>
                {pendingTheme ? (
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                  </svg>
                ) : (
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <h3 style={{color: '#f59e0b', textAlign: 'center'}}>Change Theme</h3>
              <p style={{textAlign: 'center', color: isDarkMode ? '#cbd5e1' : '#475569'}}>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
              <div style={{display: 'flex', gap: '1rem', justifyContent: 'center'}}>
                <button className={styles.cancelBtn} onClick={cancelThemeChange}>
                  Cancel
                </button>
                <button className={styles.themeBtn} onClick={confirmThemeChange}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <div className={styles.modalOverlay} onClick={cancelLogout}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
              <div className={styles.themeIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="16,17 21,12 16,7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="21" y1="12" x2="9" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 style={{ color: '#ef4444' }}>Logout</h3>
              <p>Are you sure you want to logout?</p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button className={styles.cancelBtn} onClick={cancelLogout}>Cancel</button>
                <button className={styles.themeBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PdlCellUpdate;
